import html from './template.html'

const template = document.createElement('template')
template.innerHTML = html

customElements.define(
    'read-more',
    class ReadMoreElement extends HTMLElement {
        /**
         * Fire the attributeChangedCallback on these attributes.
         */
        static get observedAttributes() {
            return ['active']
        }

        /**
         * ARIA states for indicated open and closed states to screen readers.
         * @static
         */
        static get ariaStates() {
            return {
                expanded: {
                    OPEN: 'true',
                    CLOSED: 'false',
                },
            }
        }

        /**
         * Add the Shadow DOM to the element.
         */
        constructor() {
            super()
            this.attachShadow({ mode: 'open' })
            this.shadowRoot.append(template.content.cloneNode(true))
            this.onClick = this.onClick.bind(this)
        }

        /**
         * Returns the tab element from the Shadow DOM.
         */
        get tab() {
            return this.shadowRoot.querySelector('.tab')
        }

        /**
         * Returns the panel element from the Shadow DOM.
         */
        get panel() {
            return this.shadowRoot.querySelector('.panel')
        }

        /**
         * Returns the inner element from the Shadow DOM.
         */
        get inner() {
            return this.shadowRoot.querySelector('.inner')
        }

        /**
         * Returns the inner element from the Shadow DOM.
         */
        get title() {
            return this.shadowRoot.querySelector('.title')
        }

        /**
         * Gets and sets the open-label attribute.
         * @property
         */
        get openLabel() {
            return this.getAttribute('open-label')
        }

        set openLabel(value) {
            if ('string' === typeof value) {
                this.setAttribute('open-label')
            }
        }

        /**
         * Gets and sets the close-label attribute.
         * @property
         */
        get closeLabel() {
            return this.getAttribute('close-label')
        }

        set closeLabel(value) {
            if ('string' === typeof value) {
                this.setAttribute('close-label')
            }
        }

        /**
         * Get and set the active attribute.
         * @property
         */
        get active() {
            return this.hasAttribute('active')
        }

        set active(value) {
            if ('boolean' === typeof value) {
                if (value === true) {
                    this.setAttribute('active', '')
                } else {
                    this.removeAttribute('active')
                }
            }
        }

        onClick(event) {
            this.active = !this.active
            event.preventDefault()
        }

        /**
         * Add click listener to the button on connect..
         */
        connectedCallback() {
            this.tab.addEventListener('click', this.onClick)

            if (this.title.textContent === '') {
                this.title.textContent = this.openLabel
            }
        }

        /**
         * Remove the click listener from the button on disconnect.
         */
        disconnectedCallback() {
            this.tab.removeEventListener('click', this.onClick)
        }

        /**
         * Act whenever an attribute has been modified.
         * @param {string} attrname The name of the changed attribute.
         */
        attributeChangedCallback(attrname) {
            switch (attrname) {
                case 'active':
                    if (this.active) {
                        this.open()
                        this.title.textContent = this.closeLabel
                    } else {
                        this.close()
                        this.title.textContent = this.openLabel
                    }
                    break
            }
        }

        /**
         * Asserts if the element has a transition property for max-height.
         * @private
         * @returns {boolean}
         */
        panelHasTransition() {
            const styles = window.getComputedStyle(this.panel)
            const transition = styles.getPropertyValue('transition-property')
            return transition === 'max-height'
        }

        /**
         * Opens the panel by the height of its content.
         * NOTE: All the requestAnimationFrames are needed to create a transition.
         * @returns {void}
         */
        open() {
            requestAnimationFrame(() => {
                this.tab.setAttribute(
                    'aria-expanded',
                    ReadMoreElement.ariaStates.expanded.OPEN
                )
                this.panel.style.maxHeight = '0px'
                requestAnimationFrame(() => {
                    this.panel.style.maxHeight = `${this.inner.offsetHeight}px`
                    if (this.panelHasTransition()) {
                        this.panel.addEventListener(
                            'transitionend',
                            () => {
                                this.panel.style.maxHeight = ''
                            },
                            { once: true }
                        )
                    } else {
                        requestAnimationFrame(() => {
                            this.panel.style.maxHeight = ''
                        })
                    }
                })
            })
        }

        /**
         * Closes the panel element.
         * NOTE: All the requestAnimationFrames are needed to create a transition.
         * @returns {void}
         */
        close() {
            this.panel.style.transition = 'max-height 0ms'
            requestAnimationFrame(() => {
                this.tab.setAttribute(
                    'aria-expanded',
                    ReadMoreElement.ariaStates.expanded.CLOSED
                )
                this.panel.style.maxHeight = `${this.inner.offsetHeight}px`
                requestAnimationFrame(() => {
                    this.panel.style.transition = ''
                    requestAnimationFrame(() => {
                        this.panel.style.maxHeight = '0px'
                    })
                })
            })
        }
    }
)



// WEBPACK FOOTER //
// ./scripts/web-components/read-more/index.js